import { useMemo } from 'react';
import { RelativeDatePeriod, RelativeDateType } from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import { emptyArray, getIfValidStringifiedObject } from '@fabric-ui/utils';
import { entityViewConfig } from '@utils/entityViewConfig';
import { endOfDay, startOfDay, subMonths } from 'date-fns';
import { cleanEmptyFilters, filterToExpression } from '../../utils/filterUtils';
import { useMeasurementsList } from '../Reports/hooks';

export const getDefaultStartDate = () => subMonths(startOfDay(new Date().setDate(1)), 2).toISOString();

export const ownerField = 'ticket.assignee_id';

export const defaultFilters = {
  [ownerField]: [],
  dateDimPeriod: ['3'],
  dateGap: [TimeBucket.BUCKET_MONTH],
  dateObject: {
    value: {
      to: getDefaultStartDate(),
      from: endOfDay(new Date()).toISOString(),
    },
    preset: {
      count: 3,
      period: RelativeDatePeriod.months,
      type: RelativeDateType.last,
    },
  },
  keyMetrics: ['open_tickets_granular', 'active_tickets_granular', 'over_sla_granular'],
  analyze: {
    sort: [],
    metrics: ['over_sla_percent_granular', 'over_sla_granular', 'open_tickets_granular'],
    rowDim: 'ticket.assignee_id',
    colDim: 'date',
    page: '0',
  },
};

export type AnalyzeParam = { sort: any[]; metrics: string[]; rowDim: string; colDim: string; page: string };

export const parseAnalyzeParam = (raw: unknown): AnalyzeParam | null => {
  const value = getIfValidStringifiedObject(raw as string) || (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : null);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { rowDim, colDim, metrics, sort, page } = value as Record<string, unknown>;
  if (typeof rowDim !== 'string' || !rowDim) return null;
  if (typeof colDim !== 'string' || !colDim) return null;
  const normalizedMetrics = Array.isArray(metrics) ? metrics : metrics != null ? [metrics] : null;
  if (!normalizedMetrics?.length) return null;
  return {
    rowDim,
    colDim,
    metrics: normalizedMetrics as string[],
    sort: Array.isArray(sort) ? sort : [],
    page: typeof page === 'string' ? page : '0',
  };
};

export const useRemediationMeasurementOptions = () => {
  const { data: measurements } = useMeasurementsList({
    entityName: entityViewConfig.Ticket.entityTypeId,
    filterFunc: (v: any) => v.context.includes(entityViewConfig.Ticket.entityTypeId),
  });
  return useMemo(
    () =>
      (measurements || [])
        .filter(measurement => measurement.timeExpression)
        .reduce(
          (acc, { displayName, systemName }) => {
            const selectValue = `${systemName} AS ${systemName}`;
            acc.measurementOptions = [...acc.measurementOptions, { value: systemName, selectValue, title: displayName }];
            acc.selectValueMap[systemName] = selectValue;
            return acc;
          },
          {
            measurementOptions: [],
            selectValueMap: {},
          }
        ),
    [measurements]
  );
};

export const getQueryObj = ({
  dims = emptyArray,
  metrics = emptyArray,
  filters,
  additionalFilters,
  typeMap,
  sorting = emptyArray,
  groupBy = emptyArray,
  top,
  timeRange,
  projectionId,
}: {
  dims?: any[];
  metrics?: any[];
  filters?;
  additionalFilters?;
  typeMap?;
  sorting?: any[];
  groupBy?: any[];
  top?;
  timeRange?;
  projectionId;
}) => {
  const cleanedFilters = filters && cleanEmptyFilters(filterToExpression(typeMap, filters));
  return {
    select: {
      dims,
      metrics,
    },
    filter: (additionalFilters || cleanedFilters) && {
      and: {
        operands: [...(additionalFilters ? [additionalFilters] : []), ...(cleanedFilters?.and?.operands || [])],
      },
    },
    sorting,
    groupBy,
    top,
    timeRange,
    projectionId,
  };
};
